import { NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { HttpClientModule } from '@angular/common/http';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { TypeaheadModule } from 'ngx-bootstrap/typeahead';
import { MARKED_OPTIONS, MarkdownModule } from 'ngx-markdown';

import { CoreFiltersModule} from '../core/core.filters.module'
import { ProjectIndicatorsComponent } from '../core/modules/project/projectIndicators/project.indicators.component';

import { AudioAlertComponent } from './audio-alert/audio-alert.component';
import { CommitHeaderComponent } from './commits/commit-header.component';
import { CommitListComponent } from './commits/commit-list.component';
import { ContentsComponent } from './contents/contents.component';
import { GfCaptchaComponent } from './captcha/captcha.component';
import { DiffContextService } from './diff/diff-context.service';
import { CommentFormComponent } from './diff/comment-form/comment-form.component';
import { DiffCommentsComponent } from './diff/comments/diff-comments.component';
import { DiffContextComponent } from './diff/context/diff-context.component';
import { DiffComponent } from './diff/diff/diff.component';
import { SpinnerComponent } from './spinner/spinner.component';
import { ProjectHeadingDropdownComponent } from './mega-menu/project-heading-dropdown/project-heading-dropdown.component'
import { MenuService } from './mega-menu/mega-menu.service.component';
import { ProjectMenuComponent } from './mega-menu/project-menu/project-menu.component';
import { ProjectMenuMiniComponent } from './mega-menu/project-mini/project-mini.component';
import { GlyphService } from './gf-glyph/gf-glyph.service';
import { GlyphComponent } from './gf-glyph/gf-glyph.component';
import { TimerLabelComponent } from './timer-label/timer-label.component';
import { UserLabelComponent } from './user-label/user-label.component';
import { MergeInstructionsComponent } from './merge/instructions/merge.instructions.component';
import { MergeComponent } from './merge/merge/merge.component';

import { CardContextMenuComponent } from './gf-card-board/cardContextMenu/board.card-context-menu.component';
import { GfCardComponent } from './gf-card-board/gf-card/gf-card.component';
import { KanbanCardComponent } from './gf-card-board/kanban-card/kanban.card.component';
import { GfCardBoardComponent } from './gf-card-board/gf-card-board.component';
import { UserProjectListComponent } from './user-project-list/user.project-list.component';
import { ThumbnailComponent } from './gf-thumbnail/gf-thumbnail.component';
import { TagsDisplayComponent } from './gf-tags-display/gf-tags-display.component';
import { TagsInputComponent } from './gf-tags/gf-tags.component';

import { MentionModule } from './gf-mentions/mention.module';
import { SendNotificationComponent } from './gf-send-notification/gf-send-notification.component';

import { GfMeetingService } from './gf-meet-button/gf-meeting.service';
import { MeetLinkComponent } from './gf-meet-button/meet-link/gf-meet-link.component';
import { MeetButtonComponent } from './gf-meet-button/meet-button/gf-meet-button.component';
import { LinkPreviewComponent } from './gf-link-preview/gf-link-preview.component';
import { DatePickerComponent } from './gf-date-picker/date-picker.component';

import { DynamicFilterDateComponent } from './dynamic_filters/date/dynamic-filters.date.component';
import { DynamicFiltersComponent } from './dynamic_filters/filters/dynamic-filters.component';
import { DynamicFilterSelectComponent } from './dynamic_filters/select/dynamic-filters.select.component';
import { DynamicFilterTextComponent } from './dynamic_filters/text/dynamic-filters.text.component';
import { SimpleDataTableComponent } from './simple-data-table/simple-data-table.component';
import { ProgressBarComponent } from './gf-progress-bar/gf-progress-bar.component';

import { NotificationHistoryHelper } from './gf-notification-history/gf-notification-history.service.component';
import { NotificationHistoryComponent } from './gf-notification-history/gf-notification-history.component';

import { PagerMiniComponent } from './paging/mini/paging.mini.component';
import { PagerComponent } from './paging/paging.component';
import { DrillDownMenuComponent } from './drill-down-menu/drill-down-menu.component';
import { TicklerBarComponent } from './ticklerBar/ticklerbar.component';
import { TrackerTicklerService } from './ticklerBar/tracker.tickler.service';
import { RelatedSearchService } from './related_search/related.search.service';
import { RelatedResultListComponent } from './related_search/related-result-list/related-result-list.component';
import { RelatedResultBadgeComponent } from './related_search/result-count-badge/result-count-badge.component';
import { RelatedSearchComponent } from './related_search/related-search.component';
import { ReactionsListComponent } from './reactions/reactions-list/reactions-list.component';
import { ReactionsDropdownComponent } from './reactions/reactions-dropdown/reactions-dropdown.component';

import { PopoverDirective } from './popover/popover.directive';
import { ChoiceButtonsComponent } from './module-button/choice-buttons/choice-buttons.component';
import { ModuleButtonComponent } from './module-button/module-button/module-button.component';
import { ModuleChoiceButtonsComponent } from './module-button/module-choice-buttons/module-choice-buttons.component';

import { GForgeIconComponent } from './gforge-logos/gforge-icon.component';
import { GForgeLogoComponent } from './gforge-logos/gforge-logo.component';

import { BreadcrumbService } from './breadcrumbs/breadcrumbs.service';
import { BreadcrumbsComponent } from './breadcrumbs/breadcrumbs.component';
import { MarkdownEditorService, gfMarkedOptionsFactory } from '../core/services/core.markdown-editor.service';

import { SearchTypesComponent } from './search-types/search-types.component';

import { SearchableDropdownComponent } from './gf-search-dropdown/search-dropdown.component';

import { DynamicFieldsService } from './dynamic_forms/dynamic-fields.service';
import { DynamicFieldCheckboxComponent } from './dynamic_forms/checkbox/dynamic-fields.checkbox.component';
import { DynamicFieldDateComponent } from './dynamic_forms/date/dynamic-fields.date.component';
import { DynamicFieldRadioComponent } from './dynamic_forms/radio/dynamic-fields.radio.component';
import { DynamicFieldSelectComponent } from './dynamic_forms/select/dynamic-fields.select.component';
import { DynamicFieldMultiSelectComponent } from './dynamic_forms/select-multi/dynamic-fields.multi-select.component';
import { DynamicFieldSelectSearchComponent } from './dynamic_forms/select-search/dynamic-fields.select-search.component';
import { DynamicFieldSelectSimpleComponent } from './dynamic_forms/select-simple/dynamic-fields.select-simple.component';
import { DynamicFieldSerialComponent } from './dynamic_forms/serial/dynamic-fields.serial.component';
import { DynamicFieldTextComponent } from './dynamic_forms/text/dynamic-fields.text.component';
import { DynamicFieldTextareaComponent } from './dynamic_forms/textarea/dynamic-fields.textarea.component';
import { DynamicFieldButtonDdownSelectComponent } from './dynamic_forms/button-ddown-select/button-ddown-select.component';

import { MembersSelectorComponent } from './membersSelector/members.selector.component';

import { ReminderService } from './reminders/reminder.service';
import { ReminderHelper } from './reminders/reminder.helper';
import { ReminderBadgeComponent } from './reminders/reminderBadge/reminder.badge.component';
import { RemindersAlertComponent } from './reminders/remindersAlertComponent/reminders.alert.component';

import { ReminderAddModal } from './reminders/reminderAddModal/reminder.add.modal.component';
import { RemindersComponent } from './reminders/reminders.component';

import { ContextMenuDirective } from './gf-context-menu/gf-context-menu.component';
import { FileDropDirective } from './drag-drop/file-drop.directive';
import { PanelToggleDirective } from './gf-panel-toggle/gf-panel-toggle.component';
import { TextviewComponent } from './gf-textview/gf-textview.component';
import { MonitorComponent } from './gf-monitor/monitor.component';
import { NopePanelComponent } from './nope_panel/nope_panel.component';

import { ImageModalComponent } from './gf-file-preview/image-modal/image-modal.component';
import { VideoModalComponent } from './gf-file-preview/video-modal/video-modal.component';
import { AudioModalComponent } from './gf-file-preview/audio-modal/audio-modal.component';
import { FilePreviewComponent } from './gf-file-preview/gf-file-preview.component';
import { CheckboxListComponent } from './gf-checkbox-list/gf-checkbox-list.component';

import { CommitAssociationComponent } from './gf-commit-association/gf-commit-association.component';
import { ChecksResultListComponent } from './gf-checks-result/result-list/checks-result-list.component';
import { ChecksDetailService } from './gf-checks-result/detail-list/checks-detail.service';
import { ChecksDetailListComponent } from './gf-checks-result/detail-list/checks-detail-list.component';
import { CheckTotalsComponent } from './gf-checks-result/check-totals/check-totals.component';

import { ProjectSearchComponent } from './project_search/project-search.component';
import { TemplateSelectComponent } from './project_templates/template-select.component';

import { ParentProjectLinkComponent } from './project-header-directives/parent-project-link.component';

import { TrackerItemLinkComponent } from './tracker-item-link/tracker-item-link.directive';

import { TreeViewComponent } from './tree-view/tree-view.component';
import { WikiHelperService } from './wiki/services/wiki.helper';
import { WikiService } from './wiki/services/wiki.service';
import { WikiEditService } from './wiki/services/wiki.edit.service';
import { WikiEditLifecycleService } from './wiki/services/wiki.edit-lifecycle.service';
import { WikiConflictService } from './wiki/services/wiki.conflict.service';
import { WikiViewHtmlComponent } from './wiki/view-html/wiki.view-html.component';
import { WikiVersionsComponent } from './wiki/versions/wiki.versions.component';
import { WikiContainerComponent } from './wiki/container/wiki.container.component';
import { WikiDownloadComponent } from './wiki/download/wiki.download.component';
import { WikiImportComponent } from './wiki/import/wiki.import.component';
import { WikiImportModeModalComponent } from './wiki/import/wiki.import-mode-modal.component';
import { WikiViewComponent } from './wiki/view/wiki.view.component';
import { WikiMonitorComponent } from '../plugins/wiki/monitor/wiki.monitor.component';
import { WikiReferrersComponent } from '../plugins/wiki/referrers/wiki.referrers.component';
import { WikiAttachmentsComponent } from '../plugins/wiki/attachments/wiki.attachments.component';
import { WikiDeleteModalComponent } from './wiki/wiki-delete-modal/wiki.delete.modal.component';
import { WikiRenameModalComponent } from './wiki/wiki-rename-modal/wiki.rename.modal.component';
import { WikiMarkdownEditorComponent } from './wiki/edit-markdown/wiki.markdown-editor.component';
import { WikiMarkdownGuideComponent } from './wiki/edit-help/wiki.markdown-guide.component';
import { WikiEditViewComponent } from '../plugins/wiki/edit/wiki.edit-view.component';
import { WikiAttachmentPickerModalComponent } from '../plugins/wiki/attachments/wiki.attachment-picker-modal.component';
import { WikiAttachmentQueueService } from '../plugins/wiki/attachments/wiki.attachment-queue.service';
import { FileTypeService } from '../plugins/tracker/services/file-type.service';
import { GfMarkdownEditorComponent } from './gf-markdown-editor/gf-markdown-editor.component';
import { GfMarkdownViewerComponent } from './gf-markdown-viewer/gf-markdown-viewer.component';
import { WikiDiscardConfirmationModalComponent } from './wiki/discard-confirmation-modal/wiki.discard-confirmation-modal.component';
import { WikiConvertOnSaveModalComponent } from './wiki/convert-on-save-modal/wiki.convert-on-save-modal.component';
import { WikiConflictModalComponent } from './wiki/conflict-modal/wiki.conflict-modal.component';
import { WikiVideoInsertModalComponent } from './wiki/video-insert-modal/wiki.video-insert-modal.component';

// The button for current user to toggle monitoring of wiki page
import { MonitorWikiComponent } from './wiki/monitor-toggle/wiki.monitor-toggle.component';
import { LockWikiComponent } from './wiki/lock/wiki.lock.component';
import { PublicWikiComponent } from './wiki/public/wiki.public.component';

import { TroveProjectsComponent } from './trove/trove-projects/trove.projects.component';
import { TroveContentComponent } from './trove/trove-content/trove.content.component';

import { SetFocusDirective } from './set-focus/set-focus.directive';

import { ContextMenuModule } from '@perfectmemory/ngx-contextmenu';
import { GfClipboardDirective } from './gf-clipboard/gf-clipboard'
import { UIRouterModule } from '@uirouter/angular';

import { ProjectActivityTimelineComponent } from './project-activity-timeline/project.activity.timeline.component';
import { UserTimelineComponent } from './user-activity-timeline/user.timeline.component';

import { FeatureFlagDirective } from './feature-flags/feature-flags.directive';

import { FavoritesService } from './favorites/favorites.service';
import { FavoriteButtonComponent } from './favorites/favorite-button/favorite-button.component';
import { FavoritesListComponent } from './favorites/favorites-list/favorites-list.component';
import { AiFlowSettingsComponent } from './ai-flow-settings/ai-flow-settings.component';
import { GfInlineConfirmComponent } from './gf-inline-confirm/gf-inline-confirm.component';
import { EventSubscriptionListComponent } from './event-subscription-list/event-subscription-list.component';
import { EventSubscriptionEditComponent } from './event-subscription-edit/event-subscription-edit.component';
import { EventSubscriptionModalComponent } from './event-subscription-modal/event-subscription-modal.component';
import { EventDeliveryListComponent } from './event-delivery-list/event-delivery-list.component';
import { EventDeliveryDetailComponent } from './event-delivery-detail/event-delivery-detail.component';
import { ChatStatusDotComponent } from './chat-status-dot/chat-status-dot.component';

@NgModule({
    imports: [
        BrowserModule,
        HttpClientModule,
        CoreFiltersModule,
        FormsModule,
        NgbModule,
        TypeaheadModule.forRoot(),
        ContextMenuModule,
        UIRouterModule.forChild(),
        MentionModule,
        MarkdownModule.forRoot({
            markedOptions: {
                provide: MARKED_OPTIONS,
                useFactory: gfMarkedOptionsFactory
            }
        })
    ],
    providers: [
        DiffContextService,
        MenuService,
        GlyphService,
        GfMeetingService,
        NotificationHistoryHelper,
        TrackerTicklerService,
        RelatedSearchService,
        BreadcrumbService,
        DynamicFieldsService,
        ReminderService,
        ReminderHelper,
        ChecksDetailService,
        WikiHelperService,
        WikiService,
        WikiEditService,
        WikiEditLifecycleService,
        WikiConflictService,
        WikiAttachmentQueueService,
        FileTypeService,
        FavoritesService,
        MarkdownEditorService
    ],
    exports: [
        UIRouterModule,
        MentionModule,
        AudioAlertComponent,
        ProjectIndicatorsComponent,
        CommitHeaderComponent,
        CommitListComponent,
        ContentsComponent,
        GfCaptchaComponent,
        CommentFormComponent,
        DiffCommentsComponent,
        DiffContextComponent,
        DiffComponent,
        SpinnerComponent,
        ProjectHeadingDropdownComponent,
        ProjectMenuComponent,
        ProjectMenuMiniComponent,
        GlyphComponent,
        TimerLabelComponent,
        UserLabelComponent,
        MergeInstructionsComponent,
        MergeComponent,
        CardContextMenuComponent,
        GfCardComponent,
        KanbanCardComponent,
        GfCardBoardComponent,
        UserProjectListComponent,
        ThumbnailComponent,
        TagsDisplayComponent,
        TagsInputComponent,
        SendNotificationComponent,
        MeetLinkComponent,
        MeetButtonComponent,
        LinkPreviewComponent,
        MeetLinkComponent,
        MeetButtonComponent,
        LinkPreviewComponent,
        ThumbnailComponent,
        ProgressBarComponent,
        NotificationHistoryComponent,
        DatePickerComponent,
        DynamicFilterDateComponent,
        DynamicFiltersComponent,
        DynamicFilterSelectComponent,
        DynamicFilterTextComponent,
        ProgressBarComponent,
        PagerMiniComponent,
        PagerComponent,
        SimpleDataTableComponent,
        ProgressBarComponent,
        DrillDownMenuComponent,
        TicklerBarComponent,
        SimpleDataTableComponent,
        RelatedResultListComponent,
        RelatedResultBadgeComponent,
        RelatedSearchComponent,
        ReactionsListComponent,
        ReactionsDropdownComponent,
        ChoiceButtonsComponent,
        ModuleButtonComponent,
        ModuleChoiceButtonsComponent,
        GForgeIconComponent,
        GForgeLogoComponent,
        BreadcrumbsComponent,
        SearchTypesComponent,
        SearchableDropdownComponent,
        DynamicFieldCheckboxComponent,
        DynamicFieldDateComponent,
        DynamicFieldRadioComponent,
        DynamicFieldSelectComponent,
        DynamicFieldMultiSelectComponent,
        DynamicFieldSelectSearchComponent,
        DynamicFieldSelectSimpleComponent,
        DynamicFieldSerialComponent,
        DynamicFieldTextComponent,
        DynamicFieldTextareaComponent,
        DynamicFieldButtonDdownSelectComponent,
        PopoverDirective,
        ReminderBadgeComponent,
        RemindersAlertComponent,
        ReminderAddModal,
        RemindersComponent,
        ContextMenuDirective,
        FileDropDirective,
        PanelToggleDirective,
        ImageModalComponent,
        VideoModalComponent,
        AudioModalComponent,
        FilePreviewComponent,
        FileDropDirective,
        PanelToggleDirective,
        TextviewComponent,
        MonitorComponent,
        NopePanelComponent,
        CheckboxListComponent,
        CommitAssociationComponent,
        ChecksResultListComponent,
        ChecksDetailListComponent,
        CheckTotalsComponent,
        CheckboxListComponent,
        ProjectSearchComponent,
        TemplateSelectComponent,
        ParentProjectLinkComponent,
        TrackerItemLinkComponent,
        TreeViewComponent,
        WikiViewHtmlComponent,
        WikiVersionsComponent,
        WikiContainerComponent,
        WikiDownloadComponent,
        WikiImportComponent,
        WikiImportModeModalComponent,
        WikiViewComponent,
        WikiMonitorComponent,
        WikiReferrersComponent,
        WikiAttachmentsComponent,
        MonitorWikiComponent,
        LockWikiComponent,
        PublicWikiComponent,
        WikiMarkdownEditorComponent,
        WikiMarkdownGuideComponent,
        WikiEditViewComponent,
        GfMarkdownEditorComponent,
        GfMarkdownViewerComponent,
        WikiDiscardConfirmationModalComponent,
        WikiConvertOnSaveModalComponent,
        WikiConflictModalComponent,
        TroveProjectsComponent,
        TroveContentComponent,
        SetFocusDirective,
        GfClipboardDirective,
        ProjectActivityTimelineComponent,
        UserTimelineComponent,
        FeatureFlagDirective,
        MembersSelectorComponent,
        FavoriteButtonComponent,
        FavoritesListComponent,
        AiFlowSettingsComponent,
        GfInlineConfirmComponent,
        EventSubscriptionListComponent,
        EventSubscriptionEditComponent,
        EventSubscriptionModalComponent,
        EventDeliveryListComponent,
        EventDeliveryDetailComponent,
        ChatStatusDotComponent,
    ],
    declarations: [
        AudioAlertComponent,
        ProjectIndicatorsComponent,
        CommitHeaderComponent,
        CommitListComponent,
        ContentsComponent,
        GfCaptchaComponent,
        CommentFormComponent,
        DiffCommentsComponent,
        DiffContextComponent,
        DiffComponent,
        SpinnerComponent,
        ProjectHeadingDropdownComponent,
        ProjectMenuComponent,
        ProjectMenuMiniComponent,
        GlyphComponent,
        TimerLabelComponent,
        UserLabelComponent,
        MergeInstructionsComponent,
        MergeComponent,
        CardContextMenuComponent,
        GfCardComponent,
        KanbanCardComponent,
        GfCardBoardComponent,
        UserProjectListComponent,
        ThumbnailComponent,
        TagsDisplayComponent,
        TagsInputComponent,
        SendNotificationComponent,
        MeetLinkComponent,
        MeetButtonComponent,
        LinkPreviewComponent,
        MeetLinkComponent,
        MeetButtonComponent,
        LinkPreviewComponent,
        ThumbnailComponent,
        ProgressBarComponent,
        NotificationHistoryComponent,
        DatePickerComponent,
        DynamicFilterDateComponent,
        DynamicFiltersComponent,
        DynamicFilterSelectComponent,
        DynamicFilterTextComponent,
        ProgressBarComponent,
        PagerMiniComponent,
        PagerComponent,
        SimpleDataTableComponent,
        ProgressBarComponent,
        DrillDownMenuComponent,
        TicklerBarComponent,
        SimpleDataTableComponent,
        RelatedResultListComponent,
        RelatedResultBadgeComponent,
        RelatedSearchComponent,
        ReactionsListComponent,
        ReactionsDropdownComponent,
        ChoiceButtonsComponent,
        ModuleButtonComponent,
        ModuleChoiceButtonsComponent,
        GForgeIconComponent,
        GForgeLogoComponent,
        BreadcrumbsComponent,
        SearchTypesComponent,
        SearchableDropdownComponent,
        DynamicFieldCheckboxComponent,
        DynamicFieldDateComponent,
        DynamicFieldRadioComponent,
        DynamicFieldSelectComponent,
        DynamicFieldMultiSelectComponent,
        DynamicFieldSelectSearchComponent,
        DynamicFieldSelectSimpleComponent,
        DynamicFieldSerialComponent,
        DynamicFieldTextComponent,
        DynamicFieldTextareaComponent,
        DynamicFieldButtonDdownSelectComponent,
        PopoverDirective,
        MembersSelectorComponent,
        ReminderBadgeComponent,
        RemindersAlertComponent,
        ReminderAddModal,
        RemindersComponent,
        ContextMenuDirective,
        FileDropDirective,
        PanelToggleDirective,
        ImageModalComponent,
        VideoModalComponent,
        AudioModalComponent,
        FilePreviewComponent,
        FileDropDirective,
        PanelToggleDirective,
        TextviewComponent,
        MonitorComponent,
        NopePanelComponent,
        CheckboxListComponent,
        CommitAssociationComponent,
        ChecksResultListComponent,
        ChecksDetailListComponent,
        CheckTotalsComponent,
        CheckboxListComponent,
        ProjectSearchComponent,
        TemplateSelectComponent,
        ParentProjectLinkComponent,
        TrackerItemLinkComponent,
        TreeViewComponent,
        WikiViewHtmlComponent,
        WikiVersionsComponent,
        WikiContainerComponent,
        WikiDownloadComponent,
        WikiImportComponent,
        WikiImportModeModalComponent,
        WikiViewComponent,
        WikiMonitorComponent,
        WikiReferrersComponent,
        WikiAttachmentsComponent,
        MonitorWikiComponent,
        LockWikiComponent,
        PublicWikiComponent,
        WikiMarkdownEditorComponent,
        WikiMarkdownGuideComponent,
        WikiEditViewComponent,
        WikiAttachmentPickerModalComponent,
        GfMarkdownEditorComponent,
        GfMarkdownViewerComponent,
        WikiDiscardConfirmationModalComponent,
        WikiConvertOnSaveModalComponent,
        WikiConflictModalComponent,
        WikiVideoInsertModalComponent,
        TroveProjectsComponent,
        TroveContentComponent,
        SetFocusDirective,
        GfClipboardDirective,
        ProjectActivityTimelineComponent,
        UserTimelineComponent,
        FeatureFlagDirective,
        WikiDeleteModalComponent,
        WikiRenameModalComponent,
        FavoriteButtonComponent,
        FavoritesListComponent,
        AiFlowSettingsComponent,
        GfInlineConfirmComponent,
        EventSubscriptionListComponent,
        EventSubscriptionEditComponent,
        EventSubscriptionModalComponent,
        EventDeliveryListComponent,
        EventDeliveryDetailComponent,
        ChatStatusDotComponent,
    ]
})
export class CoreWidgetsModule {
    constructor() { }
}
